import { percentileInBands } from "@/lib/utils/gauge-calculations";
import { BAND_COLORS, BAND_NAMES, TRUE_BOUNDS } from "../constants";
import type { TrendSeries } from "../types";

/**
 * Each observation's place on its gauge, from 0 to 100: where its value sits
 * among that date's band values around that date's 10-year average, worked
 * out exactly as the gauge does. Null where the payload has no bands for the
 * date. Both percentile scales plot this. The data service sends the same
 * numbers as `gauge_percentile`; this is for payloads without them.
 */
export function bandPercentiles(series: Pick<TrendSeries, "values" | "avg10y" | "bands">): (number | null)[] {
  return series.values.map((value, i) => {
    const bands = series.bands?.[i];
    const avg = series.avg10y?.[i];
    return value == null || !bands || avg == null ? null : percentileInBands(value, bands, avg);
  });
}

/** Maps a value between two sets of breakpoints, linearly within each segment. */
export function piecewiseMap(value: number | null, from: number[], to: number[]): number | null {
  if (value == null || Number.isNaN(value)) return null;

  const v = Math.max(from[0], Math.min(from[from.length - 1], value));
  for (let i = 0; i < from.length - 1; i++) {
    const a = from[i];
    const b = from[i + 1];
    if (v >= a && v <= b) return to[i] + ((v - a) / (b - a)) * (to[i + 1] - to[i]);
  }
  return to[to.length - 1];
}

/** Band index (0 = extremely low … 6 = extremely high) for a percentile. */
export function bandIndexFor(percentile: number): number {
  let band = 0;
  for (let i = 0; i < BAND_COLORS.length; i++) {
    if (percentile >= TRUE_BOUNDS[i]) band = i;
  }
  return band;
}

export function bandFor(
  percentile: number,
  colors: string[] = BAND_COLORS,
): { index: number; name: string; color: string } {
  const index = bandIndexFor(percentile);
  return { index, name: BAND_NAMES[index], color: colors[index] };
}
